import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, PenLine, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { CATEGORIES } from "@/data/constants";

const GUIDELINES = [
  "Original, factual content relevant to wolfdogs — news, legislation, care, behavior, or community.",
  "600–1,500 words. Write clearly and cite sources where possible.",
  "No hate speech, misinformation, or content that endangers animals or people.",
  "Include your name and a short author bio. We may lightly edit for clarity and style.",
  "Submissions are reviewed by our editorial team before publication.",
];

export default function GuestArticles() {
  useSeo({ title: "Guest Articles", description: "Read and submit wolfdog guest articles." });
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState({
    title: "", author_name: "", author_email: "", author_bio: "",
    category: "Community", content: "", image_url: "",
  });
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/guest-posts/published").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.author_name || !form.author_email || !form.content) {
      toast.error("Please fill in title, name, email, and content.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/guest-posts", form);
      toast.success("Thank you! Your article has been submitted for review.");
      setForm({ title: "", author_name: "", author_email: "", author_bio: "", category: "Community", content: "", image_url: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Submission failed. Please check your entries.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-14">
      <p className="overline text-[#8B6D1F]">For Guest Writers</p>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0A192F] mt-1">Articles &amp; Guides</h1>
      <p className="mt-4 text-lg text-[#4A5568] max-w-2xl leading-relaxed">
        In-depth articles on wolfdog care, training, behavior, and advocacy — contributed by our community.
      </p>

      {posts.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {posts.map((p) => (
            <Link key={p.id} to={`/articles/${p.id}`} data-testid={`guest-post-${p.id}`} className="group bg-white border border-[#0A192F]/10 p-6 hover:-translate-y-1 hover:border-[#D4AF37] transition-all">
              <span className="overline text-[#8B6D1F]">{p.category}</span>
              <h3 className="font-display text-xl font-bold text-[#0A192F] mt-2 group-hover:text-[#8B6D1F] transition-colors">{p.title}</h3>
              <p className="mt-2 text-sm text-[#4A5568] line-clamp-3 leading-relaxed">{p.excerpt}</p>
              <p className="mt-4 overline text-[#4A5568]">By {p.author_name}</p>
            </Link>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 mt-16" id="submit">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <PenLine className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="font-display text-2xl font-bold text-[#0A192F]">Submission Guidelines</h2>
          </div>
          <ul className="space-y-3">
            {GUIDELINES.map((g) => (
              <li key={g} className="flex items-start gap-3 text-sm text-[#4A5568] leading-relaxed">
                <CheckCircle2 className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" /> {g}
              </li>
            ))}
          </ul>
        </div>

        <form onSubmit={submit} className="lg:col-span-3 bg-white border border-[#0A192F]/10 p-8 space-y-4" data-testid="guest-submit-form">
          <h2 className="font-display text-2xl font-bold text-[#0A192F]">Submit Your Article</h2>
          <input value={form.title} onChange={set("title")} placeholder="Article title" data-testid="guest-title" className="w-full px-4 py-3 border border-[#0A192F]/20 outline-none focus:ring-2 focus:ring-[#D4AF37]" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input value={form.author_name} onChange={set("author_name")} placeholder="Your name" data-testid="guest-name" className="px-4 py-3 border border-[#0A192F]/20 outline-none focus:ring-2 focus:ring-[#D4AF37]" />
            <input value={form.author_email} onChange={set("author_email")} type="email" placeholder="Your email" data-testid="guest-email" className="px-4 py-3 border border-[#0A192F]/20 outline-none focus:ring-2 focus:ring-[#D4AF37]" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <select value={form.category} onChange={set("category")} data-testid="guest-category" className="px-4 py-3 border border-[#0A192F]/20 outline-none focus:ring-2 focus:ring-[#D4AF37] bg-white">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input value={form.image_url} onChange={set("image_url")} placeholder="Header image URL (optional)" data-testid="guest-image" className="px-4 py-3 border border-[#0A192F]/20 outline-none focus:ring-2 focus:ring-[#D4AF37]" />
          </div>
          <input value={form.author_bio} onChange={set("author_bio")} placeholder="Short author bio (optional)" data-testid="guest-bio" className="w-full px-4 py-3 border border-[#0A192F]/20 outline-none focus:ring-2 focus:ring-[#D4AF37]" />
          <textarea value={form.content} onChange={set("content")} rows={9} placeholder="Write your article here..." data-testid="guest-content" className="w-full px-4 py-3 border border-[#0A192F]/20 outline-none focus:ring-2 focus:ring-[#D4AF37]" />
          <button type="submit" disabled={loading} data-testid="guest-submit-button" className="inline-flex items-center gap-2 bg-[#0A192F] text-white overline px-7 py-3 hover:bg-[#0A192F]/90 transition-colors disabled:opacity-60">
            <Send className="w-4 h-4" /> {loading ? "Submitting..." : "Submit for Review"}
          </button>
        </form>
      </div>
    </div>
  );
}
